import React, { useState, useMemo } from 'react';
import { Trash2, RotateCw, CheckCircle, XCircle, AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Address } from '../types/index';

interface AddressTableProps {
  addresses: Address[];
  onUpdateAddress: (id: string, updates: Partial<Address>) => void;
  onDeleteAddress: (id: string) => void;
  onGeocodeAddress: (id: string) => void;
  showCoordinates: boolean;
  isGeocoding: boolean;
}

type SortField = 'businessName' | 'address' | 'latitude' | 'longitude' | 'status';
type SortDirection = 'asc' | 'desc';

const AddressTable: React.FC<AddressTableProps> = ({
  addresses,
  onUpdateAddress,
  onDeleteAddress,
  onGeocodeAddress,
  showCoordinates,
  isGeocoding
}) => {
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleCellClick = (id: string, field: string, currentValue: string) => {
    setEditingCell({ id, field });
    setEditValue(currentValue);
  };

  const handleCellSave = () => {
    if (editingCell) {
      onUpdateAddress(editingCell.id, { [editingCell.field]: editValue });
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellSave();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const getGeocodingStatus = (address: Address) => {
    if (!address.address) return null;
    if (address.latitude && address.longitude) return 'success';
    return 'pending';
  };

  const renderGeocodingStatus = (address: Address) => {
    const status = getGeocodingStatus(address);
    
    if (!status) return null;
    
    switch (status) {
      case 'success':
        return (
          <CheckCircle 
            size={14} 
            className="text-green-600"
            aria-label="Successfully geocoded"
          />
        );
      case 'pending':
        return (
          <AlertCircle 
            size={14} 
            className="text-yellow-600"
            aria-label="Needs geocoding"
          />
        );
      default:
        return null;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAddresses = useMemo(() => {
    if (!sortField) return addresses;

    return [...addresses].sort((a, b) => {
      let aValue: string | number | null = '';
      let bValue: string | number | null = '';

      switch (sortField) {
        case 'businessName':
          aValue = a.businessName || '';
          bValue = b.businessName || '';
          break;
        case 'address':
          aValue = a.address || '';
          bValue = b.address || '';
          break;
        case 'latitude':
          aValue = a.latitude || 0;
          bValue = b.latitude || 0;
          break;
        case 'longitude':
          aValue = a.longitude || 0;
          bValue = b.longitude || 0;
          break;
        case 'status':
          const statusA = getGeocodingStatus(a);
          const statusB = getGeocodingStatus(b);
          aValue = statusA === 'success' ? '1' : statusA === 'pending' ? '2' : '3';
          bValue = statusB === 'success' ? '1' : statusB === 'pending' ? '2' : '3';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [addresses, sortField, sortDirection]);

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown size={14} className="text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp size={14} className="text-blue-600" /> : 
      <ChevronDown size={14} className="text-blue-600" />;
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ 
    field, 
    children, 
    className = "px-1 py-0.5 text-left text-xs font-medium text-gray-500 uppercase" 
  }) => (
    <th className={className}>
      <button
        onClick={() => handleSort(field)}
        className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
        title={`Sort by ${field}`}
      >
        <span>{children}</span>
        {renderSortIcon(field)}
      </button>
    </th>
  );

  return (
    <div className="flex-1 overflow-hidden">
      <div className="h-full overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="w-12 px-1 py-0.5 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
              <SortableHeader field="status" className="w-16 px-1 py-0.5 text-center text-xs font-medium text-gray-500 uppercase">
                Status
              </SortableHeader>
              <SortableHeader field="businessName">
                Business Name
              </SortableHeader>
              <SortableHeader field="address">
                Address
              </SortableHeader>
              {showCoordinates && (
                <>
                  <SortableHeader field="latitude">
                    Latitude
                  </SortableHeader>
                  <SortableHeader field="longitude">
                    Longitude
                  </SortableHeader>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAddresses.map((address, index) => (
              <tr
                key={address.id}
                id={`address-row-${address.id}`}
                className={`hover:bg-gray-50 ${
                  address.isHighlighted ? 'bg-yellow-100' : ''
                } ${address.isDuplicate ? 'bg-red-50' : ''} ${
                  address.isOnRoute ? 'bg-green-50' : ''
                } ${address.isSelected ? 'bg-blue-50 border-l-4 border-blue-400' : ''}`}
                style={{ height: '28px' }}
              >
                <td className="px-1 py-0.5">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => onDeleteAddress(address.id)}
                      className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100 transition-colors"
                      title="Delete this address row"
                    >
                      <Trash2 size={12} />
                    </button>
                    {address.address && (
                      <button
                        onClick={() => onGeocodeAddress(address.id)}
                        disabled={isGeocoding}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100 disabled:text-gray-400 transition-colors"
                        title="Re-geocode this address"
                      >
                        <RotateCw size={12} className={isGeocoding ? 'animate-spin' : ''} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-1 py-0.5 text-center">
                  {renderGeocodingStatus(address)}
                </td>
                <td className="px-1 py-0.5">
                  {editingCell?.id === address.id && editingCell?.field === 'businessName' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleCellSave}
                      onKeyDown={handleKeyPress}
                      className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={() => handleCellClick(address.id, 'businessName', address.businessName)}
                      className="cursor-pointer min-h-[20px] py-1 px-2 hover:bg-gray-100 rounded text-sm truncate"
                      title={address.businessName}
                    >
                      {address.businessName || 'Click to edit'}
                    </div>
                  )}
                </td>
                <td className="px-1 py-0.5">
                  {editingCell?.id === address.id && editingCell?.field === 'address' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleCellSave}
                      onKeyDown={handleKeyPress}
                      className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={() => handleCellClick(address.id, 'address', address.address)}
                      className={`cursor-pointer min-h-[20px] py-1 px-2 hover:bg-gray-100 rounded text-sm truncate ${
                        address.latitude && address.longitude ? 'text-green-600 font-medium' : ''
                      }`}
                      title={address.address}
                    >
                      {address.address || 'Click to edit'}
                    </div>
                  )}
                </td>
                {showCoordinates && (
                  <>
                    <td className="px-1 py-0.5 text-xs text-gray-600">
                      {address.latitude ? address.latitude.toFixed(6) : '-'}
                    </td>
                    <td className="px-1 py-0.5 text-xs text-gray-600">
                      {address.longitude ? address.longitude.toFixed(6) : '-'}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AddressTable;